import type { ClassValue } from "clsx";

import clsx from "clsx";

export type TextButtonSize = "xl" | "md" | "sm" | "xs" | "2xs";
export type TextButtonIconSize = "xs" | "sm" | "md";
export type ResponsiveTextButtonValue<T> = T | { base: T; lg?: T };
export type TextButtonTone = "primary" | "link" | "warning";
export type TextButtonFocusStyle = "ring" | "outline";

const baseDefault = [
  "inline-flex",
  "items-center",
  "bg-surface-transparent",
] as const;

const focusStyles = {
  ring: [
    "focus-visible:outline-none",
    "focus-visible:ring-2",
    "focus-visible:ring-offset-2",
    "focus-visible:ring-stroke-focused",
  ],
  outline: [
    "rounded",
    "focus-visible:outline",
    "focus-visible:outline-2",
    "focus-visible:outline-offset-2",
    "focus-visible:outline-stroke-focused",
  ],
} as const satisfies Record<TextButtonFocusStyle, readonly string[]>;

const baseIconSizing = ["[&>svg]:shrink-0"] as const;

const baseDisabled = ["text-text-disabled", "cursor-not-allowed"] as const;

export const textButtonToneClasses = {
  primary: "text-text-primary",
  link: "text-text-link-default",
  warning: "text-text-warning-default",
} as const satisfies Record<TextButtonTone, string>;

const typoSizes = {
  xl: ["typo-label-xl"],
  md: ["typo-label-md"],
  sm: ["typo-label-sm"],
  xs: ["typo-label-xs"],
  "2xs": ["typo-label-2xs"],
} as const satisfies Record<TextButtonSize, readonly string[]>;

const defaultIconSizes = {
  xl: "md",
  md: "sm",
  sm: "sm",
  xs: "xs",
  "2xs": "xs",
} as const satisfies Record<TextButtonSize, TextButtonIconSize>;

const iconSizes = {
  md: ["[&>svg]:h-6", "[&>svg]:w-6"],
  sm: ["[&>svg]:h-4", "[&>svg]:w-4"],
  xs: ["[&>svg]:h-3", "[&>svg]:w-3"],
} as const satisfies Record<TextButtonIconSize, readonly string[]>;

const lgIconSizes = {
  md: ["lg:[&>svg]:h-6", "lg:[&>svg]:w-6"],
  sm: ["lg:[&>svg]:h-4", "lg:[&>svg]:w-4"],
  xs: ["lg:[&>svg]:h-3", "lg:[&>svg]:w-3"],
} as const satisfies Record<TextButtonIconSize, readonly string[]>;

const gapSizes = {
  xl: ["gap-2"],
  md: ["gap-2"],
  sm: ["gap-2"],
  xs: ["gap-1"],
  "2xs": ["gap-1"],
} as const satisfies Record<TextButtonSize, readonly string[]>;

const lgGapSizes = {
  xl: ["lg:gap-2"],
  md: ["lg:gap-2"],
  sm: ["lg:gap-2"],
  xs: ["lg:gap-1"],
  "2xs": ["lg:gap-1"],
} as const satisfies Record<TextButtonSize, readonly string[]>;

export const textButtonStyles = {
  container: ({
    size = "xl",
    iconSize,
    tone = "primary",
    focusStyle = "ring",
    underlined = false,
    isDisabled = false,
    className,
  }: {
    size?: ResponsiveTextButtonValue<TextButtonSize>;
    iconSize?: ResponsiveTextButtonValue<TextButtonIconSize>;
    tone?: TextButtonTone;
    focusStyle?: TextButtonFocusStyle;
    underlined?: boolean;
    isDisabled?: boolean;
    className?: ClassValue;
  }) => {
    const baseSize = typeof size === "string" ? size : size.base;
    const lgSize = typeof size === "string" ? undefined : size.lg;
    const baseIconSize =
      typeof iconSize === "string"
        ? iconSize
        : (iconSize?.base ?? defaultIconSizes[baseSize]);
    const lgIconSize =
      typeof iconSize === "string"
        ? undefined
        : iconSize
          ? iconSize.lg
          : lgSize && defaultIconSizes[lgSize];

    return clsx(
      baseDefault,
      gapSizes[baseSize],
      lgSize && lgGapSizes[lgSize],
      baseIconSizing,
      iconSizes[baseIconSize],
      lgIconSize && lgIconSizes[lgIconSize],
      focusStyles[focusStyle],
      typeof size === "string" && typoSizes[size],
      isDisabled ? baseDisabled : textButtonToneClasses[tone],
      underlined && "underline",
      !isDisabled && (underlined ? "hover:no-underline" : "hover:underline"),
      className,
    );
  },
};
